import { computed, ref } from 'vue';

const MIN_COMPLETED_DATE = new Date('0002-01-01T00:00:00Z');
const dateFormatter = new Intl.DateTimeFormat('en-GB');

export function useClientList(clients) {
  const filters = ref({ ProjectStatus: '' });
  const searchTerm = ref('');
  const sort = ref({ key: 'clientName', direction: 'asc' });

  const visibleClients = computed(() => {
    let result = Array.isArray(clients.value) ? [...clients.value] : [];

    result = applySearch(result, searchTerm.value);
    result = applyFilters(result, filters.value);
    result = applySorting(result, sort.value);

    return result;
  });

function handleFilters(newFilters) {
  filters.value = {
    ...filters.value,
    ...newFilters,
  };

  searchTerm.value = newFilters?.ClientName ?? '';
}

  function toggleSort(key) {
    if (!key) return;

    if (sort.value.key === key) {
      sort.value = {
        key,
        direction: sort.value.direction === 'asc' ? 'desc' : 'asc',
      };

      return;
    }

    sort.value = {
      key,
      direction: 'asc',
    };
  }

  return {
    filters,
    searchTerm,
    sort,
    visibleClients,
    handleFilters,
    toggleSort,
  };
}

function applySearch(clients, term) {
  const query = String(term ?? '')
    .trim()
    .toLocaleLowerCase();

  if (!query) {
    return clients;
  }

  return clients.filter((client) =>
    String(client?.clientName ?? '')
      .toLocaleLowerCase()
      .includes(query),
  );
}

function applyFilters(clients, filters) {
  let result = clients;

  const projectStatus = filters?.ProjectStatus;

  if (projectStatus === 'Complete') {
    result = result.filter(isProjectComplete);
  }

  if (projectStatus === 'Active') {
    result = result.filter(
      (client) => !isProjectComplete(client),
    );
  }

  const clientType = filters?.ClientType;

  if (clientType === 'Direct') {
    result = result.filter(
      (client) => client?.directClient === true,
    );
  }

  if (clientType === 'Intermediary') {
    result = result.filter(
      (client) => client?.directClient !== true,
    );
  }

  return result;
}
function applySorting(clients, sort) {
  if (!sort?.key) {
    return clients;
  }

  const direction = sort.direction === 'desc' ? -1 : 1;

  return [...clients].sort((left, right) => {
    const leftValue = getSortValue(left, sort.key);
    const rightValue = getSortValue(right, sort.key);

    if (leftValue == null && rightValue == null) {
      return 0;
    }

    if (leftValue == null) {
      return 1;
    }

    if (rightValue == null) {
      return -1;
    }

    if (
      typeof leftValue === 'number' &&
      typeof rightValue === 'number'
    ) {
      return (leftValue - rightValue) * direction;
    }

    return (
      String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * direction
    );
  });
}

function getSortValue(client, key) {
  switch (key) {
    case 'clientName':
      return client?.clientName ?? '';

    case 'milestone':
      return client?.currentMilestone?.type ?? '';

    case 'customerType':
      return getClientType(client);

    case 'status':
      return getStatusPill(client).label;

    case 'goLiveDate': {
      const timestamp = Date.parse(client?.goLiveDate);

      return Number.isNaN(timestamp)
        ? null
        : timestamp;
    }

    default:
      return client?.[key] ?? '';
  }
}

export function isProjectComplete(client) {
  const completed = client?.currentMilestone?.completed;

  if (!completed) {
    return false;
  }

  const completedDate = new Date(completed);

  return (
    !Number.isNaN(completedDate.getTime()) &&
    completedDate > MIN_COMPLETED_DATE
  );
}

export function getStatusPill(client) {
  if (isProjectComplete(client)) {
    return {
      label: 'Complete',
      class: 'complete',
    };
  }

  const scheduledCompletion =
    client?.currentMilestone?.scheduledCompletion;

  const scheduledDate = scheduledCompletion
    ? new Date(scheduledCompletion)
    : null;

  const isOverdue =
    scheduledDate &&
    !Number.isNaN(scheduledDate.getTime()) &&
    scheduledDate < new Date();

  return isOverdue
    ? {
        label: 'At Risk',
        class: 'overdue',
      }
    : {
        label: 'On Track',
        class: 'in-progress',
      };
}

export function getClientType(client) {
  if (client?.directClient === true) {
    return 'Direct Client';
  }

  return client?.intermediaryName || '—';
}

export function formatClientDate(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? '—'
    : dateFormatter.format(date);
}